/*
 * ATYP personalization add-on (Path A).
 *
 * A small, dependency-free web component that powers the opt-in engraving + special requests panel
 * on the product page. It owns only the disclosure UI and the character counter, and exposes three
 * methods that product-form.js calls at submit time:
 *
 *   normalize()      disable any empty personalization input so it never serialises (keeps the
 *                    default Add to cart byte-identical when the panel is unused or left blank).
 *   isActive()       true only when the panel is open and engraving text is present and a fee
 *                    variant is configured; this is what makes product-form.js add the fee line.
 *   getFeeItem(qty)  the second cart line item ({id, quantity, properties}) for the fixed fee.
 *
 * The engraving/special-request inputs live in the DOM bound to the product form via the form=
 * attribute, so their values reach the piece line as line item properties with no extra wiring.
 * Colour from the standard palette is never handled here; it stays on the variant picker. The
 * special requests field only carries non-standard asks (custom size, colour change, other notes).
 */
if (!customElements.get('personalization-addon')) {
  customElements.define(
    'personalization-addon',
    class PersonalizationAddon extends HTMLElement {
      constructor() {
        super();
        this.opened = false;
      }

      connectedCallback() {
        this.trigger = this.querySelector('.atyp-perz__trigger');
        this.panel = this.querySelector('.atyp-perz__panel');
        this.engravingInput = this.querySelector('[data-perz-engraving]');
        this.requestInput = this.querySelector('[data-perz-request]');
        this.counter = this.querySelector('[data-perz-count]');
        this.max = this.engravingInput ? parseInt(this.engravingInput.getAttribute('maxlength'), 10) || 0 : 0;

        if (this.trigger) this.trigger.addEventListener('click', () => this.toggle());
        if (this.engravingInput) this.engravingInput.addEventListener('input', () => this.updateCount());
      }

      toggle() {
        this.opened ? this.close() : this.open();
      }

      open() {
        this.opened = true;
        if (this.panel) this.panel.hidden = false;
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        this.setEnabled(true);
        if (this.engravingInput) this.engravingInput.focus();
      }

      close() {
        this.opened = false;
        if (this.panel) this.panel.hidden = true;
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        this.setEnabled(false);
      }

      setEnabled(on) {
        [this.engravingInput, this.requestInput].forEach((el) => {
          if (el) el.disabled = !on;
        });
      }

      updateCount() {
        if (this.counter && this.max && this.engravingInput) {
          this.counter.textContent = this.engravingInput.value.length + '/' + this.max;
        }
      }

      // Called by product-form.js just before it reads the form. Empty fields are disabled so they
      // never serialise; a field with a value stays enabled and travels with the order.
      normalize() {
        [this.engravingInput, this.requestInput].forEach((el) => {
          if (!el) return;
          el.disabled = !this.opened || el.value.trim() === '';
        });
      }

      isActive() {
        return !!(
          this.opened &&
          this.engravingInput &&
          this.engravingInput.value.trim() !== '' &&
          this.dataset.feeVariantId
        );
      }

      getFeeItem(quantity) {
        var id = this.dataset.feeVariantId;
        if (!id) return null;
        var properties = {};
        if (this.dataset.pieceTitle) properties['For'] = this.dataset.pieceTitle;
        return { id: id, quantity: quantity || 1, properties: properties };
      }
    }
  );
}
